/* eslint-disable no-unused-vars */
import { createContext } from 'react';
import { toJS } from 'mobx';
import { t } from '@lingui/macro';
import { CatalogAgent } from 'agents';
import { commonStore, paymentStore } from 'stores';
import { plan_is_yearly, plan_is_quarterly } from '../helpers/plans.js';
import { reportError } from 'errorReporting';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import * as help from 'helpers';
import { PAYMENT } from 'config/payment.js';

class CatalogStore {
    catalogs: any = {};

    /** @var {object} */
    catalogsCoupon: any = {};

    removeFreeTrial(plan: any) {
        if (plan.adyen) {
            plan.adyen = {
                ...plan.adyen,
                free_trial_available: false,
            };
        }

        return plan;
    }

    // currently backend supports: 0, 7, and 14, true, false
    // true means 7 and false 0, but those are deprecated
    setFreeTrialDuration({
        plan,
        numberOfFreeTrialDays,
    }: {
        plan: any;
        numberOfFreeTrialDays: number;
    }) {
        plan = {
            ...plan,
            free_trial: numberOfFreeTrialDays,
        };

        return plan;
    }

    updateGiftInfo(plan: any) {
        paymentStore.setPlusTaxIsNeeded({ plan });
        const subscriptionIndex = plan.info.findIndex(
            ({ filter }: { filter: string }) =>
                filter && filter.includes('subscription'),
        );

        if (!help.is_gift_checkout_page() || subscriptionIndex === -1) {
            return plan;
        }

        const info = plan.info.map((item: any, index: number) => {
            if (index !== subscriptionIndex) {
                return item;
            }
            return {
                ...item,
                value: `${plan.price.currency}${plan.price.amount} ${
                    paymentStore.plusTaxIsNeeded ? t`+ tax` : ''
                }`,
                label: t`Charged now`,
            };
        });

        // turns "year" to "12 months" or "/ 3 months" to "3 months"
        const getGiftPeriod = ({ plan = {} }: { plan: any }) => {
            if (!plan.price) return '';

            return plan.price.period.includes('year')
                ? t`12 months`
                : plan.price.period === '/ month'
                ? t`1 month`
                : plan.price.period.replace('/ ', '');
        };

        info.splice(0, 0, {
            value: t`${getGiftPeriod({ plan })} of Premium+`,
            label: t`Gift`,
        });

        return { ...plan, info };
    }

    // how much a plan costs per month? Even if it's billed yearly or quarterly,
    // sometimes we'd like to show the price per month to users, to make it easier to compare plans
    breakPricesIntoMonthly(plan: any, active = true) {
        if (!active) {
            plan.break_price_to_monthly = false;
            return plan;
        }

        if (plan_is_yearly(plan) || plan_is_quarterly(plan)) {
            plan.break_price_to_monthly = true;
        }

        return plan;
    }

    decoratePlan(plan: any) {
        return help.plan_decorate(plan, commonStore.userPolicies);
    }

    filterYearlyPlan(plan: any) {
        return plan.id.indexOf('year') >= 0;
    }

    // keep plans with licensed contents, if it's allowed for the user
    filterLicensedContent(plan: any) {
        // is user allowed to buy licensed_content plans?
        if (commonStore.userPolicies.licensed_content) {
            return plan.id.indexOf('licensed') >= 0;
        }
        return true;
    }

    filterByMonthlyInstallments(plan: any) {
        const id = plan.core_plan_id;
        const withoutMonthly = PAYMENT.INSTALLMENT_PLAN_IDS;

        return withoutMonthly.indexOf(id) < 0;
    }

    previews(catalogs: any, coupon = null) {
        if (!catalogs.upgrades) {
            catalogs.upgrades = [];
        }

        if (!catalogs.resubscription) {
            catalogs.resubscription = [];
        }

        if (!catalogs.free_upgrades) {
            catalogs.free_upgrades = [];
        }

        const { upgrades, resubscription, free_upgrades } = catalogs;
        const canAskForPreviewsFromBackend = () =>
            (upgrades.length ||
                resubscription.length ||
                free_upgrades.length) &&
            // subscriptions that get preimum from admin cannot call previews, as they don't have a gateway
            !get(commonStore, 'subscription.given_by_admin', false) &&
            // on gift page we don't need to ask for previews, as anyone should be able to buy gifts, no augmentation from backend is needed
            !help.is_gift_page() &&
            (get(commonStore, 'subscription.is_upgradable') ||
                get(commonStore, 'subscription.can_resubscribe'));
        if (canAskForPreviewsFromBackend()) {
            const key = upgrades.length
                ? 'upgrades'
                : resubscription.length
                ? 'resubscription'
                : 'free_upgrades';
            const plans = catalogs[key];

            return catalogStore
                .getPreviews(
                    plans.map((p: any) => p.core_plan_id),
                    coupon,
                )
                .then((previews: any) => {
                    previews.forEach((preview: any) => {
                        const { new_plan, current_plan } = preview;
                        const original = plans.find(
                            (p: any) => p.core_plan_id === new_plan.id,
                        );
                        const index = plans.indexOf(original);
                        // add "remaining" key to plan
                        if (original.adyen) {
                            original.adyen.remaining = {
                                amount: Math.abs(current_plan.amount),
                                charged: new_plan.amount,
                                discount: new_plan.discount,
                            };
                        } else {
                            original.adyen = {
                                remaining: {
                                    amount: Math.abs(current_plan.amount),
                                    charged: new_plan.amount,
                                    discount: new_plan.discount,
                                },
                            };
                        }

                        plans[index] = original;
                    });

                    catalogs[key] = plans;

                    return catalogs;
                })
                .catch((error) => {
                    reportError(
                        'YS Asking for previews failed, account is probably not upgradable',
                        error,
                    );
                    console.log(
                        'YS Asking for previews failed, account is probably not upgradable',
                        error,
                    );
                });
        }

        return catalogs;
    }

    setCatalogs(catalogs: any, coupon: string | null = null) {
        if (coupon) {
            this.catalogsCoupon[coupon] = catalogs;
        } else {
            this.catalogs = catalogs;
        }
    }

    retrieveCatalogs(coupon: string) {
        if (coupon) {
            return CatalogAgent.categories(coupon)
                .catch((e) => {})
                .then((catalogs) => catalogStore.setCatalogs(catalogs, coupon));
        }

        return CatalogAgent.categories()
            .then(catalogStore.previews)
            .catch((e) => {
                console.log('YS', e);
            })
            .then((catalogs) => catalogStore.setCatalogs(catalogs));
    }

    getPreviews(ids: string[], coupon?: string | null) {
        return CatalogAgent.previews(ids, coupon);
    }

    // put premium plus first
    sortByLicenseContent(p: any) {
        return p.licensed ? -1 : 1;
    }
    // TODO check how this works, I guess it should be a.price.number - b.price.number
    sortByPrice(a: any, b: any) {
        return a.price.number - b.price.number;
    }

    resubscribe(
        discount: number = 0,
        useAlternativeOffers = false,
        breakPricesIntoMonthly = true,
        clone = false,
    ) {
        let resubscription = get(catalogStore, 'catalogs.resubscription', []);

        if (clone) {
            resubscription = toJS(resubscription);
        }

        return resubscription
            .map((p: any) => (discount ? this.applyDiscount(p, discount) : p))
            .map((p: any) =>
                this.breakPricesIntoMonthly(p, breakPricesIntoMonthly),
            )
            .map(this.decoratePlan)
            .sort(this.sortByPrice);
    }

    blackFriday() {
        let plans = get(catalogStore, 'catalogs.black_friday');

        // when backend doesn't return anything in black_friday, we know that user is not eligible for it
        if (!Array.isArray(plans) || isEmpty(plans)) return [];

        const BLACK_FRIDAY_DISCOUNT_PERCENTAGE = 50;

        return plans
            .map((p) => this.applyDiscount(p, BLACK_FRIDAY_DISCOUNT_PERCENTAGE))
            .map(this.decoratePlan);
    }

    aprilDiscount() {
        return catalogStore.blackFriday();
    }

    partnershipJason() {
        let plans = get(catalogStore, 'catalogs.black_friday');

        // when backend doesn't return anything in black_friday, we know that user is not eligible for it
        if (!Array.isArray(plans) || isEmpty(plans)) return [];

        const PARTNERSHIP_DISCOUNT_PERCENTAGE = 25;

        return plans
            .map((p) => this.applyDiscount(p, PARTNERSHIP_DISCOUNT_PERCENTAGE))
            .map(this.decoratePlan);
    }

    yousician25() {
        // use black-friday catalog
        let plans = get(catalogStore, 'catalogs.black_friday');

        // when backend doesn't return anything in black_friday, we know that user is not eligible for it
        if (!Array.isArray(plans) || isEmpty(plans)) return [];

        const YOUSICIAN_25_DISCOUNT_PERCENTAGE = 25;
        // figure out if this is an upgrade case based on the catalogs response from backend
        const subscriptionWillBeUpgraded = () =>
            get(catalogStore, 'catalogs.upgrades.length') ||
            get(catalogStore, 'catalogs.free_upgrades.length');

        if (subscriptionWillBeUpgraded()) {
            plans = catalogStore.upgrade(
                YOUSICIAN_25_DISCOUNT_PERCENTAGE,
                false,
                true,
            );

            // remove plans that we don't like to offer in this campaign
            plans = plans
                .filter(catalogStore.filterYearlyPlan)
                .filter(catalogStore.filterLicensedContent);

            return plans;
        }

        // figure out if this is a resubscribe case based on the catalogs response from backend
        const subscriptionWillBeResubscribed = () =>
            get(catalogStore, 'catalogs.resubscription.length');

        // if an itunes subscription is resubscribing
        const isFromItunesToAdyen = () =>
            get(catalogStore, 'catalogs.resubscription[0].itunes') != null;

        if (subscriptionWillBeResubscribed() && !isFromItunesToAdyen()) {
            plans = catalogStore.resubscribe(
                YOUSICIAN_25_DISCOUNT_PERCENTAGE,
                false,
                true,
            );
            // remove plans that we don't like to offer in this campaign
            plans = plans
                .filter(catalogStore.filterYearlyPlan)
                .filter(catalogStore.filterLicensedContent);

            return plans;
        }

        return plans
            .map((p) => this.applyDiscount(p, YOUSICIAN_25_DISCOUNT_PERCENTAGE))
            .map(this.decoratePlan);
    }

    newYear() {
        let plans = get(catalogStore, 'catalogs.new_year');

        // when backend doesn't return anything in new_year, we know that user is not eligible for it
        if (!Array.isArray(plans) || isEmpty(plans)) return [];

        const NEW_YEAR_DISCOUNT_PERCENTAGE = 30;

        return plans
            .map((p) => this.applyDiscount(p, NEW_YEAR_DISCOUNT_PERCENTAGE))
            .map(this.decoratePlan);
    }

    backToSchool() {
        // because they are similar campaigns we can use blackFriady
        return catalogStore.blackFriday();
    }

    gifting() {
        const plans = get(catalogStore, 'catalogs.gifting', []);

        return plans
            .slice(0, 3)
            .map(this.removeFreeTrial)
            .map(this.decoratePlan)
            .map(this.updateGiftInfo);
    }

    upgrade(
        discount: number = 0,
        useAlternativeOffers = false,
        breakPricesIntoMonthly = true,
        clone = false,
    ) {
        const upgrades = get(catalogStore, 'catalogs.upgrades', []);
        const free_upgrades = get(catalogStore, 'catalogs.free_upgrades', []);

        let plans = upgrades.length ? upgrades : free_upgrades;

        if (!plans) {
            plans = [];
        }

        if (clone) {
            plans = toJS(plans);
        }

        return plans
            .map((p: any) => (discount ? this.applyDiscount(p, discount) : p))
            .map((p: any) =>
                this.breakPricesIntoMonthly(p, breakPricesIntoMonthly),
            )
            .map(this.decoratePlan)
            .sort(this.sortByPrice);
    }

    isCouponValid(coupon: string) {
        return !!catalogStore.catalogsCoupon[coupon];
    }

    applyDiscount(plan: any, percentage: any) {
        const { adyen, core_plan_data } = plan;

        const price = (adyen && adyen.price) || core_plan_data.price || {};
        return {
            ...plan,
            adyen: {
                ...adyen,
                price: {
                    ...price,
                    discount: {
                        calculated_price:
                            price.amount - price.amount * (percentage / 100),
                        fixed: 0,
                        percentage: percentage,
                    },
                },
            },
        };
    }

    /*Sort from cheapest per month to most expensive, based on the calculated monthly price of subscription e.g. yearly price / 12 */
    sortByMonthlyPrice(planA: any, planB: any) {
        return (
            planA.price.numeric_per_month_calculated_in_frontend -
            planB.price.numeric_per_month_calculated_in_frontend
        );
    }

    trials(discount: number) {
        let plans = get(catalogStore, 'catalogs.savusauna', []);

        return plans
            .filter(this.filterByMonthlyInstallments)
            .map((p: any) => this.applyDiscount(p, discount))
            .map(this.decoratePlan);
    }

    winback() {
        let plans = get(catalogStore, 'catalogs.winback');

        // when backend doesn't return anything in winback, we know that user is not eligible for it
        if (!Array.isArray(plans) || isEmpty(plans)) return [];

        const WINBACK_DISCOUNT_PERCENTAGE = 25;

        return plans
            .map((p) => this.applyDiscount(p, WINBACK_DISCOUNT_PERCENTAGE))
            .map(this.decoratePlan)
            .sort(this.sortByLicenseContent)
            .reverse();
    }

    premiumOffer({ discountPercentage }: { discountPercentage: number }) {
        if (!catalogStore.catalogs) {
            return [];
        }

        const plans = get(catalogStore, 'catalogs.premium_offer', []);
        return plans
            .map((p: any) => this.applyDiscount(p, discountPercentage))
            .map(this.decoratePlan)
            .sort(this.sortByMonthlyPrice);
    }

    worldMusicDay({ discountPercentage }: { discountPercentage: number }) {
        if (!catalogStore.catalogs) {
            return [];
        }

        const plans = get(catalogStore, 'catalogs.world_music_day', []);
        return plans
            .map((p: any) => this.applyDiscount(p, discountPercentage))
            .map(this.decoratePlan)
            .sort(this.sortByMonthlyPrice);
    }

    // prepare possible plans that user can subscribe to during this campaign
    freeTrialOffer() {
        if (!catalogStore.catalogs) {
            // to avoid crashing for users with admin given premium days, they don't have any catalogs
            // TODO guard it completely somewhere else, so that users with admin given days don't end up here at all, or anywhere else that needs catalogs
            return [];
        }

        const plans = get(catalogStore, 'catalogs.free_trial_offer', []);
        const THIRTY_DAYS_FREE_TRIAL = 30;
        return (
            plans
                .map((plan: any) => this.breakPricesIntoMonthly(plan, true))
                .map(this.decoratePlan)
                // add extra trial days to the plan
                .map((plan: any) =>
                    this.setFreeTrialDuration({
                        plan,
                        numberOfFreeTrialDays: THIRTY_DAYS_FREE_TRIAL,
                    }),
                )
                .sort(this.sortByMonthlyPrice)
        );
    }

    // prepare possible plans that user can subscribe to during this campaign
    freeTrialOffer7() {
        if (!catalogStore.catalogs) {
            // to avoid crashing for users with admin given premium days, they don't have any catalogs
            // TODO guard it completely somewhere else, so that users with admin given days don't end up here at all, or anywhere else that needs catalogs
            return [];
        }

        const plans = get(catalogStore, 'catalogs.free_trial_offer_7', []);
        const ONE_WEEK_FREE_TRIAL_IN_DAYS = 7;
        return (
            plans
                .map((plan: any) => this.breakPricesIntoMonthly(plan, true))
                .map(this.decoratePlan)
                // set free trial duration
                .map((plan: any) =>
                    this.setFreeTrialDuration({
                        plan,
                        numberOfFreeTrialDays: ONE_WEEK_FREE_TRIAL_IN_DAYS,
                    }),
                )
                .sort(this.sortByMonthlyPrice)
        );
    }

    // prepare possible plans that user can subscribe to during this campaign
    freeTrialOffer14() {
        if (!catalogStore.catalogs) {
            // to avoid crashing for users with admin given premium days, they don't have any catalogs
            // TODO guard it completely somewhere else, so that users with admin given days don't end up here at all, or anywhere else that needs catalogs
            return [];
        }

        const plans = get(catalogStore, 'catalogs.free_trial_offer_14', []);
        const TWO_WEEK_FREE_TRIAL_IN_DAYS = 14;
        return (
            plans
                .map((plan: any) => this.breakPricesIntoMonthly(plan, true))
                .map(this.decoratePlan)
                // set free trial duration
                .map((plan: any) =>
                    this.setFreeTrialDuration({
                        plan,
                        numberOfFreeTrialDays: TWO_WEEK_FREE_TRIAL_IN_DAYS,
                    }),
                )
                .sort(this.sortByMonthlyPrice)
        );
    }

    subscribe({
        coupon = null,
        discount = null,
        freeTrials = true,
        breakPricesIntoMonthly = true,
        clone = false,
    }) {
        let plans = get(catalogStore, 'catalogs.free_users', []);

        if (coupon) {
            let catalogCoupons = get(
                catalogStore,
                `catalogsCoupon[${coupon}]`,
                {},
            );
            plans = catalogCoupons.free_users || [];
        }

        if (clone) {
            plans = toJS(plans);
        }

        return plans
            .filter(this.filterByMonthlyInstallments)
            .map((p: any) => (freeTrials ? p : this.removeFreeTrial(p)))
            .map((p: any) => (discount ? this.applyDiscount(p, discount) : p))
            .map((p: any) =>
                this.breakPricesIntoMonthly(p, breakPricesIntoMonthly),
            )
            .map(this.decoratePlan)
            .sort(this.sortByPrice);
    }
}

export const catalogStore = new CatalogStore();
export default createContext(catalogStore);
